import { useCallback, useEffect, useMemo } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import {
  MembershipRead,
  TripListRead,
  TripRead,
  useLazyMembersListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { useShouldShowRideOrderingPortalFeatures } from 'src/features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';

import { setCachedMemberships, setSelectedTripRows } from '../../trip.slice';
import { columns, rideOrderingPortalColumns } from './columns';
import { getRowClassName } from './getRowClassName';
import { TripEmptyState } from './TripEmptyState';
import { usePaginatedTripsListQuery } from './usePaginatedTripsListQuery';

export function TripManagementDataGrid() {
  const dispatch = useAppDispatch();

  const shouldShowRideOrderingPortalFeatures =
    useShouldShowRideOrderingPortalFeatures();
  const columnsToUse = useMemo(() => {
    return shouldShowRideOrderingPortalFeatures
      ? rideOrderingPortalColumns
      : columns;
  }, [shouldShowRideOrderingPortalFeatures]);

  const {
    tripsListQueryState: { count, trips, isFetching, isLoading },
    pagination,
    handlePaginationChange,
  } = usePaginatedTripsListQuery();

  const selectedTripRows = useAppSelector(
    (state) => state.trip.selectedTripRows
  );
  const selectedTripRowsAsBoolean = useMemo(() => {
    const result: Record<string, boolean> = {};
    Object.keys(selectedTripRows).forEach((id) => {
      result[id] = !!selectedTripRows[id];
    });
    return result;
  }, [selectedTripRows]);
  const setRowSelection = useCallback(
    (setter: (update: Record<string, boolean>) => Record<string, boolean>) => {
      const next: Record<string, TripListRead> = {};

      const result = setter(selectedTripRowsAsBoolean);
      Object.keys(result).forEach((id) => {
        const value = result[id];
        const trip = trips.find((t) => t.id === id) ?? null;

        if (value && trip) {
          next[id] = trip;
        }
      });

      dispatch(setSelectedTripRows(next as Record<string, TripRead>));
    },
    [dispatch, selectedTripRowsAsBoolean, trips]
  );

  const [fetchMembers] = useLazyMembersListQuery();
  const fetchAndCacheMembers = useCallback(async () => {
    const { data } = await fetchMembers({
      page: 1,
      pageSize: 1000,
    });

    if (!data) {
      return;
    }

    dispatch(
      setCachedMemberships(
        data.results.reduce((acc, member) => {
          acc[member.id] = member;
          return acc;
        }, {} as Record<string, MembershipRead>)
      )
    );
  }, [dispatch, fetchMembers]);
  useEffect(() => {
    if (trips.length) {
      fetchAndCacheMembers();
    }
  }, [fetchAndCacheMembers, trips.length]);

  const getRowId = useCallback((row: TripListRead | TripRead) => row.id, []);

  if (isLoading || isFetching) {
    return (
      <div className="my-72 flex justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <DataGrid
      tableId="trip-management"
      dataTestId="trip-management"
      columns={columnsToUse}
      data={trips}
      pagination={pagination}
      totalCount={count}
      handlePaginationChange={handlePaginationChange}
      rowSelection={selectedTripRowsAsBoolean}
      setRowSelection={
        setRowSelection as React.Dispatch<
          React.SetStateAction<Record<string | number | symbol, boolean>>
        >
      }
      getRowId={getRowId}
      emptyState={<TripEmptyState />}
      rowClassName={getRowClassName}
    />
  );
}
